import React, { useEffect, useMemo, useState } from 'react';
import { FiMinus, FiPlus } from 'react-icons/fi';
import { getPurchaseItems } from '../utils/purchaseItems';
import {
  buildReceiptItemDisplayName,
  formatReceiptMoney,
  formatVoucherQuantity,
} from '../utils/receiptFormatting';
import { buildScaleReadingBadge, getScaleDisplayWeight } from '../utils/scalePresentation';
import { getReceiptPromotionLines } from '../utils/receiptPromotion';
import ScaleWeighingSummary from './ScaleWeighingSummary';
import './PosClientView.css';

const DISPLAY_SIZE_KEY = 'pos-client-display-size';
const MIN_DISPLAY_SIZE = 80;
const MAX_DISPLAY_SIZE = 200;
const DEFAULT_DISPLAY_SIZE = 100;
const DISPLAY_SIZE_STEP = 10;

const readDisplaySize = () => {
  try {
    const savedSize = Number(window.localStorage.getItem(DISPLAY_SIZE_KEY));
    if (Number.isFinite(savedSize) && savedSize >= MIN_DISPLAY_SIZE && savedSize <= MAX_DISPLAY_SIZE) {
      return Math.round(savedSize / DISPLAY_SIZE_STEP) * DISPLAY_SIZE_STEP;
    }
  } catch {
    // The display controls remain usable when browser storage is unavailable.
  }
  return DEFAULT_DISPLAY_SIZE;
};

const resolveItemSubtotal = (item) => {
  const subtotal = Number(item?.subtotal);
  if (Number.isFinite(subtotal)) return subtotal;
  const unitPrice = Number(item?.price || 0) - Number(item?.discount || 0);
  return Math.floor(unitPrice * Number(item?.quantity || 0));
};

const resolveItemImageSrc = (item) => (
  item?.priceImage
  || item?.varietyImage
  || item?.productImage
  || ''
);

const resolveItemFallbackLetter = (displayName) => (
  String(displayName || 'Producto').trim().charAt(0).toUpperCase() || '?'
);

const PosClientView = ({
  purchase,
  loading,
  error,
  connectionStatus,
  hasEditor,
  scaleState,
  weighing,
}) => {
  const [displaySize, setDisplaySize] = useState(readDisplaySize);
  useEffect(() => {
    try {
      window.localStorage.setItem(DISPLAY_SIZE_KEY, String(displaySize));
    } catch {
      // Keep the current size for this visit even if it cannot be saved.
    }
  }, [displaySize]);

  const purchaseItems = useMemo(() => getPurchaseItems(purchase), [purchase]);
  const itemsSubtotal = Number(purchase?.itemsSubtotal || 0);
  const totalAmount = Number(purchase?.totalAmount || 0);
  const orderDiscountAmount = Number(purchase?.orderDiscountAmount || 0);
  const orderDiscountPercentage = Number(purchase?.orderDiscountPercentage || 0);
  const purchaseHeading = purchase?.id ? `Venta #${purchase.id}` : 'Venta nueva';
  const shouldShowScalePanel = Boolean(
    weighing
    || scaleState?.enabled
    || (scaleState?.connectionStatus && scaleState.connectionStatus !== 'idle')
  );
  const scaleBadge = useMemo(() => buildScaleReadingBadge(scaleState, {
    enabled: shouldShowScalePanel,
  }), [scaleState, shouldShowScalePanel]);
  const weighingWeightKg = getScaleDisplayWeight(scaleState);
  const hasSelectedWeighing = Boolean(weighing);
  const previewPending = weighing?.weightKg != null && Number(weighing.weightKg) !== Number(weighingWeightKg);
  const weighingTitle = hasSelectedWeighing
    ? weighing?.varietyName || weighing?.productName || 'Producto en balanza'
    : 'Balanza';
  const weighingMeta = hasSelectedWeighing
    ? [weighing?.productName, weighing?.metricUnit]
      .filter((value, index, items) => Boolean(value) && items.indexOf(value) === index && value !== weighingTitle)
      .join(' · ')
    : '';
  const weighingCard = scaleBadge ? (
    <section className={`pos-client-scale rounded-2xl border px-4 py-3 shadow-md backdrop-blur ${scaleBadge.tone}`}>
      <div className="pos-client-scale-layout">
        <div className="pos-client-scale-heading min-w-0">
          <h2 className="break-words pos-client-text-base font-semibold">{weighingTitle}</h2>
          {weighingMeta ? <p className="mt-0.5 break-words pos-client-text-xs opacity-75">{weighingMeta}</p> : null}
          {!previewPending && weighing?.promotionName ? (
            <p className="mt-2 break-words pos-client-text-sm font-semibold text-emerald-800">
              Promoción: {weighing.promotionName}
            </p>
          ) : null}
        </div>
        <ScaleWeighingSummary
          weightKg={weighingWeightKg}
          unitPrice={hasSelectedWeighing && !previewPending ? weighing?.unitPrice : null}
          subtotal={previewPending ? null : weighing?.subtotal}
          amountTitle="Peso"
          allowZeroAmount
          className="pos-client-scale-summary"
          cardClassName="rounded-xl bg-white/45 px-3 py-2"
          valueClassName="pos-client-scale-value mt-1 font-black tracking-tight"
        />
      </div>
    </section>
  ) : null;

  const renderPlaceholder = (content, className) => (
    <div className={`rounded-3xl border bg-white px-6 py-20 text-center shadow-sm ${className}`}>
      {content}
    </div>
  );

  let purchaseContent = null;

  if (loading) {
    purchaseContent = renderPlaceholder(
      <div className="text-gray-500">Cargando venta activa...</div>,
      'border-gray-200'
    );
  } else if (error) {
    purchaseContent = renderPlaceholder(
      <div className="text-red-700">No se pudo cargar la venta activa.</div>,
      'border-red-200 bg-red-50'
    );
  } else if (!purchase) {
    purchaseContent = renderPlaceholder(
      <>
        <div className="pos-client-text-sm font-semibold uppercase tracking-wide text-gray-400">Vista cliente</div>
        <div className="mt-3 pos-client-text-2xl font-semibold text-gray-800">
          {hasEditor ? 'No hay una venta activa para mostrar.' : 'No hay un editor POS conectado.'}
        </div>
        <div className="mt-2 pos-client-text-sm text-gray-500">
          Estado conexión: {connectionStatus === 'connected' ? 'conectado' : connectionStatus}
        </div>
      </>,
      'border-dashed border-gray-300'
    );
  } else {
    purchaseContent = (
      <div className="pos-client-purchase">
        <section className="pos-client-items min-w-0 rounded-3xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-6 py-5">
            <div className="pos-client-text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Venta activa</div>
            <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="pos-client-text-xl font-semibold text-gray-700">{purchaseHeading}</h2>
                <p className="mt-1 pos-client-text-sm text-gray-500">
                  {purchase?.Client?.name || 'Sin cliente'}
                </p>
              </div>
              <div className="max-w-full rounded-2xl bg-blue-50 px-4 py-3 text-right">
                <div className="pos-client-text-xs font-semibold uppercase tracking-wide text-blue-500">Total</div>
                <div className="mt-1 pos-client-text-3xl font-bold text-blue-700">${formatReceiptMoney(totalAmount)}</div>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {purchaseItems.map((item, index) => {
              const subtotal = resolveItemSubtotal(item);
              const unitPrice = item?.promotionSnapshot?.weightPriceId && Number(item.quantity) > 0
                ? subtotal / Number(item.quantity) : Number(item?.price || 0) - Number(item?.discount || 0);
              const itemDisplayName = buildReceiptItemDisplayName(item);
              const imageSrc = resolveItemImageSrc(item);
              const promotionLines = getReceiptPromotionLines(item);
              return (
                <div
                  key={`${item?.id || item?.varietyId || index}-${index}`}
                  className={`px-6 py-4 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/80'}`}
                >
                  <div className="pos-client-item-row">
                    <div className="pos-client-item-details flex min-w-0 items-start gap-4">
                      <div className="pos-client-item-image flex shrink-0 overflow-hidden rounded-2xl bg-gray-100 shadow-sm ring-1 ring-gray-200">
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt={itemDisplayName}
                            loading="lazy"
                            decoding="async"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center pos-client-text-xl font-bold text-gray-400">
                            {resolveItemFallbackLetter(itemDisplayName)}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="break-words pos-client-text-lg font-semibold leading-snug text-gray-900">
                          {itemDisplayName}
                        </div>
                        <div className="mt-1 pos-client-text-lg font-bold text-gray-700">
                          {item?.metricUnit || 'unidad'} · ${formatReceiptMoney(unitPrice)} x {formatVoucherQuantity(item?.quantity)}
                        </div>
                        {promotionLines.length > 0 && (
                          <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 pos-client-text-sm text-emerald-800">
                            {promotionLines.map((line, lineIndex) => (
                              <div key={lineIndex} className={lineIndex === 0 ? 'font-bold' : ''}>{line}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="pos-client-item-subtotal min-w-0">
                      <div className="pos-client-text-sm font-semibold uppercase tracking-wide text-gray-400">Subtotal</div>
                      <div className="mt-1 pos-client-text-xl font-bold text-gray-900">${formatReceiptMoney(subtotal)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <aside className="pos-client-receipt min-w-0 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="pos-client-text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Comprobante</div>
          <div className="mt-5 space-y-4">
            <div className="pos-client-receipt-row pos-client-text-base text-gray-600">
              <span>Subtotal</span>
              <span className="font-semibold text-gray-900">${formatReceiptMoney(itemsSubtotal)}</span>
            </div>
            <div className="pos-client-receipt-row pos-client-text-base text-red-600">
              <span>Descuento</span>
              <span className="font-semibold">
                -${formatReceiptMoney(orderDiscountAmount)}
                {orderDiscountPercentage > 0 ? ` (${orderDiscountPercentage}%)` : ''}
              </span>
            </div>
            <div className="border-t border-dashed border-gray-200 pt-4">
              <div className="pos-client-receipt-row pos-client-text-lg font-semibold text-gray-700">
                <span>Total</span>
                <span className="pos-client-text-3xl font-bold text-blue-700">${formatReceiptMoney(totalAmount)}</span>
              </div>
            </div>
          </div>
          <div className="mt-8 rounded-2xl bg-gray-50 px-4 py-4 pos-client-text-sm text-gray-500">
            Los productos y totales se actualizan en tiempo real mientras la venta se edita.
          </div>
        </aside>
      </div>
    );
  }

  return (
    <div className="pos-client-view">
      <div className="pos-client-content space-y-4" style={{ '--pos-client-scale': displaySize / 100 }}>
        {weighingCard}
        {purchaseContent}
      </div>
      <div className="pos-client-size-controls" role="group" aria-label="Tamaño de la vista cliente">
        <span className="px-2 text-xs font-semibold text-gray-500">Tamaño</span>
        <button
          type="button"
          className="pos-client-size-button"
          aria-label="Reducir tamaño"
          title="Reducir tamaño"
          disabled={displaySize <= MIN_DISPLAY_SIZE}
          onClick={() => setDisplaySize((size) => Math.max(MIN_DISPLAY_SIZE, size - DISPLAY_SIZE_STEP))}
        >
          <FiMinus size={20} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="pos-client-size-button pos-client-size-reset"
          aria-label="Restablecer tamaño al 100%"
          title="Restablecer tamaño al 100%"
          onClick={() => setDisplaySize(DEFAULT_DISPLAY_SIZE)}
        >
          <span aria-live="polite" aria-atomic="true">{displaySize}%</span>
        </button>
        <button
          type="button"
          className="pos-client-size-button pos-client-size-increase"
          aria-label="Aumentar tamaño"
          title="Aumentar tamaño"
          disabled={displaySize >= MAX_DISPLAY_SIZE}
          onClick={() => setDisplaySize((size) => Math.min(MAX_DISPLAY_SIZE, size + DISPLAY_SIZE_STEP))}
        >
          <FiPlus size={20} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default PosClientView;
